import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, Loader2, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { analytics } from "@/lib/analytics";

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }

    // Fetch order data
    const fetchOrderData = async () => {
      try {
        const { data, error } = await supabase
          .from("customer_orders")
          .select("*")
          .eq("stripe_session_id", sessionId)
          .single();

        if (error) throw error;
        setOrderData(data);
        
        // Track order completion
        if (data) {
          analytics.orderCompleted(
            data.id,
            data.amount_paid / 100,
            data.currency || 'EUR',
            data.pack_type || 'unknown',
            data.customer_email
          );
        }
      } catch (error) {
        console.error("Error fetching order:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [sessionId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
          <p className="text-muted-foreground">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-mesh relative overflow-hidden">
      {/* Background icon patterns */}
      <div className="absolute inset-0 opacity-5 pointer-events-none overflow-hidden">
        {["👁️", "📞", "💰", "✅", "🎬", "✨"].map((icon, i) => (
          <span
            key={i}
            className="absolute text-6xl animate-float"
            style={{
              left: `${(i * 20) % 100}%`,
              top: `${(i * 30) % 100}%`,
              animationDelay: `${i * 0.2}s`,
            }}
          >
            {icon}
          </span>
        ))}
      </div>

      <Navbar />

      <div className="relative z-10 flex items-center justify-center px-4 py-20">
        <div className="max-w-2xl w-full text-center space-y-8 animate-fade-in">
          {/* Success Icon with Animation */}
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-neon-green/20 rounded-full blur-2xl animate-glow-pulse-green scale-110" />
            <CheckCircle className="w-24 h-24 mx-auto text-neon-green relative animate-scale-in drop-shadow-[0_0_20px_rgba(74,222,128,0.5)]" />
            <Sparkles className="w-8 h-8 absolute -top-2 -right-2 text-amber animate-pulse" />
            <Sparkles className="w-6 h-6 absolute -bottom-1 -left-1 text-neon-cyan animate-pulse" style={{ animationDelay: "0.3s" }} />
          </div>
          
          {/* Success Message */}
          <div className="space-y-4">
            <h1 className="font-heading font-bold text-4xl md:text-5xl bg-gradient-to-r from-neon-purple via-neon-pink to-primary bg-clip-text text-transparent animate-text-shimmer">
              Your Content Journey Has Begun!
            </h1>
            <p className="text-2xl font-bold bg-gradient-to-r from-neon-green to-neon-cyan bg-clip-text text-transparent">
              €{orderData?.amount_paid ? (orderData.amount_paid / 100).toFixed(0) : "0"} ✓
            </p>
            <p className="text-lg text-foreground/90 max-w-xl mx-auto">
              Thank you, <span className="font-bold text-neon-purple">{orderData?.business_name}</span>! Your order has been confirmed. We'll start creating your content immediately.
            </p>
          </div>

          {/* Order Summary Card */}
          {orderData && (
            <div className="glass-morphism border border-neon-purple/30 rounded-2xl p-8 text-left space-y-6 hover-lift backdrop-blur-xl">
              <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neon-purple to-neon-pink flex items-center justify-center animate-glow-pulse">
                  <span className="text-xl">📦</span>
                </div>
                <h3 className="font-heading font-bold text-xl text-foreground">
                  Order Summary
                </h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                  <span className="text-muted-foreground">Package:</span>
                  <span className="font-bold text-neon-purple capitalize text-lg">
                    {orderData.pack_type}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                  <span className="text-muted-foreground">Language:</span>
                  <span className="font-semibold text-foreground">{orderData.video_language}</span>
                </div>
                {orderData.add_ons && orderData.add_ons.length > 0 && (
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-muted-foreground">Add-ons:</span>
                    <span className="font-semibold text-neon-cyan">{orderData.add_ons.length} selected</span>
                  </div>
                )}
                <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-semibold text-foreground text-sm">{orderData.customer_email}</span>
                </div>
              </div>
            </div>
          )}

          {/* Timeline Card */}
          <div className="glass-morphism border border-neon-cyan/30 rounded-2xl p-8 text-left space-y-6 backdrop-blur-xl relative overflow-hidden">
            {/* Icon background pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
              {["⏰", "🎬", "✅"].map((icon, i) => (
                <span
                  key={i}
                  className="absolute text-5xl"
                  style={{
                    left: `${(i * 40) % 100}%`,
                    top: `${(i * 35) % 80}%`,
                  }}
                >
                  {icon}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-3 border-b border-white/10 pb-4 relative">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neon-cyan to-primary flex items-center justify-center animate-glow-pulse">
                <span className="text-xl">📅</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground">
                What's Next
              </h3>
            </div>
            
            <div className="space-y-5 relative">
              <div className="flex items-start gap-4 p-4 bg-neon-green/10 border border-neon-green/30 rounded-xl">
                <CheckCircle className="w-6 h-6 text-neon-green mt-0.5 flex-shrink-0 animate-glow-pulse-green" />
                <div>
                  <p className="font-bold text-foreground text-lg">Today</p>
                  <p className="text-sm text-muted-foreground">
                    We begin strategizing your content and gathering insights
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-neon-purple to-neon-pink flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">📅</span>
                </div>
                <div>
                  <p className="font-bold text-foreground text-lg">Day 1-2</p>
                  <p className="text-sm text-muted-foreground">
                    Content creation in progress with AI-driven optimization
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-neon-cyan to-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs">🎬</span>
                </div>
                <div>
                  <p className="font-bold text-foreground text-lg">Day 3 (72 hours)</p>
                  <p className="text-sm text-muted-foreground">
                    Your videos + posters delivered ready to post!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            onClick={() => navigate("/")}
            size="lg"
            className="w-full sm:w-auto bg-gradient-to-r from-neon-purple via-neon-pink to-primary text-white font-heading font-bold text-lg px-12 py-6 rounded-xl hover:scale-105 transition-all duration-300 animate-glow-pulse shadow-[0_0_30px_rgba(168,85,247,0.4)]"
          >
            Back to Home
          </Button>

          {/* Additional Info */}
          <p className="text-sm text-muted-foreground max-w-md mx-auto pt-4">
            Check your email <span className="font-semibold text-neon-cyan">{orderData?.customer_email}</span> for order confirmation and next steps.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderSuccess;
